import { h, Component } from 'preact';
import Store from 'store/store';

import 'store/bolt/header';
import { accessNested, joinClasses } from 'utils/utils';
import { getPercentageThreshold } from 'utils/visibilityChecks';
import { isDualViewEnabled, isVideosPage } from 'utils/bolt';
import { getIsSinglePageIframed, getIsShowtimesOnlyIframed } from 'utils/bolt/client';
import getAppRegions from 'utils/bolt/appRegions';
import { smoothScrollTo } from 'utils/smoothScroll';
import HTML from 'components/core/html/html.js';
// import { share } from 'utils/bolt/header';

import Button from 'components/bolt/button/button';
import Link from 'components/bolt/link/link';
import BurgerMenu from 'components/bolt/burgerMenu/burgerMenu';
import Share from 'components/bolt/icons/share';
import Close from 'components/bolt/icons/close';
import ArrowDown from 'components/bolt/icons/arrowDown';
import HeaderExpanded from 'components/bolt/header/headerExpanded';
import SharePopout from 'components/bolt/header/sharePopout';
import { animation } from 'utils/bolt/transitions';

import s from 'components/bolt/header/header.sss';

export default class Header extends Component {

	constructor(props) {
		super(props);

		this.toggleShare = this.toggleShare.bind(this);
		this.openNavMenu = this.openNavMenu.bind(this);
		this.showShowtimes = this.showShowtimes.bind(this);
		this.showHomeEnt = this.showHomeEnt.bind(this);
		this.showCalendarRemind = this.showCalendarRemind.bind(this);
		this.triggerBurgerBtn = this.triggerBurgerBtn.bind(this);
		this.triggerActionButton = this.triggerActionButton.bind(this);
		this.renderExtraActions = this.renderExtraActions.bind(this);
		this.toggleRegionSelector = this.toggleRegionSelector.bind(this);
		this.getRegionList = this.getRegionList.bind(this);
	}

	componentDidMount() {
		const store = Store.get();

		if (store.limitExceeded) {
			Store.on('UPDATE_API_KEY', this.getRegionList);
			return;
		}
		this.getRegionList();

		const dualViewEnabled = isDualViewEnabled();
		const hasDynamicButton = accessNested(store, 'appData.header.powsterHeader.persistentBtn.path', '') === '@dynamicPriority';
		if (dualViewEnabled && hasDynamicButton) {
			Store.emit('FILTERS_REFRESH', { forceShowtimesRefresh: true });
		}
	}

	componentWillUnmount() {
		Store.off('UPDATE_API_KEY', this.getRegionList);
	}

	async getRegionList() {
		let regions = await getAppRegions();
		if (regions) {
			// Prevent modifications of the original regions list when adding label etc.
			regions = JSON.parse(JSON.stringify(regions));
		} else {
			regions = {};
		}

		const data = this.props.appData?.header?.powsterHeader?.regionSelector;
		const extraRegions = data?.extraRegions || {};
		const specificRegions = data?.disabledRegions?.toLowerCase()?.split(',')?.map(e => e.trim())?.filter(e => e) || [];
		const includeToggle = !!data?.includeRegionsToggle;

		Object.entries(extraRegions).forEach(([countryCode, data]) => {
			regions[countryCode.toLowerCase()] = JSON.parse(JSON.stringify(data));
		});

		Object.entries(regions).forEach(([key]) => {
			if (includeToggle !== specificRegions.includes(key)) {
				delete regions[key];
			}
		});

		let currentRegion = accessNested(this.props, 'appData.meta.region.slug');
		const useLanguage = data?.useLanguage;
		let lang = this.props?.appData?.meta?.region?.languageFull || this.props?.appData?.meta?.region?.language;
		if (useLanguage && lang && regions[currentRegion] && !regions[currentRegion].label) {
			regions[currentRegion].label = lang;
		}

		this.setState({ regions });
	}

	openNavMenu() {
		if (!this.state.regions && !Store.get().limitExceeded) {
			this.getRegionList();
		}

		Store.emit('OPEN_NAV_MENU');
	}

	showShowtimes() {
		Store.emit('SHOWTIMES_SHOW', true);
	}

	showHomeEnt() {
		Store.emit('HOMEENT_SHOW', true);
	}

	showCalendarRemind() {
		Store.emit('SHOW_CAL_REMIND');
	}

	toggleRegionSelector() {
		this.setState({ regionSelectorShowing: !this.state.regionSelectorShowing });
	}

	getCurrentHeaderAction() {
		let actions = this.props.headerData.actions;
		return actions[actions.length - 1] || {
			icon: 'burger',
			action: this.openNavMenu
		};

	}

	getCurrentActionBtnAction(homeEntOpen) {
		let hidden = false;
		try {
			const { appData } = this.props;
			const previewPanelReplacingHomePage = appData?.pages.boltPreviewPanel && !appData?.pages.boltHome;
			hidden = !Object.values(appData.pages).find(page => {
				return page && !page.type && !page.section && (page.page === 'home');
			}) && !previewPanelReplacingHomePage;
		} catch (e) { }

		if (homeEntOpen) {
			return {
				icon: 'close',
				action: () => Store.emit('HOMEENT_HIDE', true),
				hidden
			};
		}

		const store = Store.get();

		if (store.widgetFullyExpanded) {
			return {
				icon: 'close',
				action: () => Store.emit('EXIT_WIDGET', true),
				hidden: false
			};
		}

		if (store.pressKit?.show) {
			return {
				icon: 'close',
				action: () => Store.emit('PRESSKIT_HIDE', true),
				hidden: false
			};
		}

		const actions = accessNested(store, 'showtimes.actions', []);
		let lastAction = actions[actions.length - 1];
		if (lastAction) {
			return lastAction;
		}
		// Default action
		return {
			icon: 'close',
			action: () => Store.emit('SHOWTIMES_HIDE', true),
			hidden
		};
	}

	shouldHideActionBtn(actionData, showtimes, homeEnt, pressKit) {
		const interstitialOpen = this.props.headerData?.interstitialOpen;
		if (actionData.hidden || interstitialOpen) return true;
		if (actionData.icon === 'back') return false;
		const isSinglePageIframed = getIsSinglePageIframed();
		if ((homeEnt || showtimes || pressKit) && isSinglePageIframed) return true;
	}

	isPersistentBtnExcluded(btn) {
		if (!btn.pageExclusions) return false;
		const exclusions = btn.pageExclusions.split(',').map(id => id.trim());
		const activePage = Store.get().activePage;
		return (exclusions.includes(activePage));
	}

	getSectionSpecificStyle() {
		const activeSection = this.props.headerData?.sectionUnderHeader || this.props.headerData?.activeSection;
		const customHeaderColorsPerSection = this.props.appData.styling.variables?.customHeaderColorsPerSection;
		if (!customHeaderColorsPerSection || !customHeaderColorsPerSection[activeSection]) {
			return null;
		}
		return customHeaderColorsPerSection[activeSection];
	}

	triggerBurgerBtn() {
		Store.emit('POP_HEADER_ACTION');
		let actionData = this.getCurrentHeaderAction();
		if (typeof actionData.action === 'function') {
			actionData.action();
		} else if (typeof actionData.action === 'string') {
			Store.emit(actionData.action);
		}
	}

	triggerActionButton(homeEntVisible) {
		let actionData = this.getCurrentActionBtnAction(homeEntVisible);
		if (typeof actionData.action === 'function') {
			actionData.action();
		} else if (typeof actionData.action === 'string') {
			Store.emit(actionData.action);
		}
		Store.emit('POP_SHOWTIMES_BTN_ACTION');
	}

	async triggerPersistentButton(path, label, sameWindow) {
		Store.emit('HEADER_PERSISTENT_BUTTON_CLICK', 'header-button-click', 'header', label, {}, path);
		if (!path) {
			return;
		}
		// Use navigateInternally from utils/bolt ?
		// We should also use actual links instead of buttons+onclick for external links
		if (path.startsWith('http')) {
			let link = document.createElement('a');
			link.href = path;
			link.target = sameWindow ? '_self' : '_blank';
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else if (path.startsWith('@')) {
			return Store.emit('OPEN_RESERVED_VARIABLE_LINK', path);
		} else if (isVideosPage(path)) {
			return Store.emit('OPEN_VIDEO_OVERLAY', path);
		} else if (path === 'boltShowtimes') {
			const hasShowtimesWidget = Object.values(this.props.appData?.pages).find(page => page.type === 'stickAndScaleWidget' && page.data.type === 'showtimes');
			if (hasShowtimesWidget) {
				return Store.emit('CLOSE_AND_NAVIGATE', hasShowtimesWidget.id, '', { instant: false });
			} else {
				return Store.emit('SHOWTIMES_SHOW', true);
			}
		} else if (path === 'boltHomeEnt') {
			return Store.emit('HOMEENT_SHOW', true);
		} else if (path === 'boltPressKit') {
			return Store.emit('PRESSKIT_SHOW', true);
		} else if (path === 'scrollTop') {
			smoothScrollTo(document.getElementById('mainScroll'), 0, 300);
		} else {
			const mainScroll = document.getElementById('mainScroll');
			const logo = label.toLowerCase() === 'logo';
			let pageData = this.props.appData?.pages?.[this.props.activePage];
			const hasHubPage = this.props.appData.pages['boltHubPage'];
			const isRootPath = pageData?.path === '/';
			const isSection = pageData?.section;
			if (isRootPath && logo) {
				return smoothScrollTo(document.getElementById('mainScroll'), 0, 300);
			}
			if (!isSection) {
				if (hasHubPage) {
					await animation(mainScroll, logo ? 'right' : 'left', 400);
				}
			}
			return Store.emit('CLOSE_AND_NAVIGATE', path, '', { instant: false });
		}
	}

	async toggleShare() {
		const sharePopoutOpen = accessNested(this.props, 'headerData.shareOpen', false);
		const webShareEnabled = accessNested(this.props, 'appData.header.powsterHeader.share.webShare', false);
		const device = this.props?.client?.device || '';
		const isMobile = device !== 'desktop' && device !== 'tablet';

		if (!sharePopoutOpen && webShareEnabled && isMobile && navigator.share) {
			Store.emit('CLICKED_MOBILE_NATIVE_SHARE');
			const title = accessNested(this.props, 'appData.copy.baseCopy.$META_TITLE', '');
			const url = window.location.href;
			const text = accessNested(this.props, 'appData.copy.baseCopy.$SOCIAL_COPY', '');

			try {
				await navigator.share({ text, title, url });
			} catch (err) {
				console.log('Share failed:', err.message);
			}
			return;
		}

		Store.emit('TOGGLE_SHARE_OPEN');

		if (!sharePopoutOpen) {
			Store.emit('DISABLE_SCROLLING_LOCK');
			return;
		}
		Store.emit('ENABLE_SCROLLING_LOCK');

	}

	renderExtraActions() {
		const showtimesVisible = accessNested(this.props, 'headerData.showtimesOverlay', false);
		const homeEntVisible = accessNested(this.props, 'headerData.homeEntOverlay', false);
		const pressKitVisible = accessNested(this.props, 'headerData.pressKitOverlay', false);
		const homeEntMerchantOpen = accessNested(this.props, 'headerData.homeEntMerchantOpen', false);
		let actionData = this.getCurrentActionBtnAction(homeEntVisible);

		const pages = accessNested(this.props, 'appData.pages');
		const onlyShowtimesAndVideo = Object.keys(pages).every(p => p === 'boltShowtimes' || p === 'boltVideos');
		const store = Store.get();
		const lastAction = accessNested(store, 'showtimes.actions', []).length > 0;

		const isDesktopSize = accessNested(this.props, 'client.isDesktopSize');

		const accessibilityCopy = accessNested(this.props, 'appData.copy.accessibilityCopy', {});

		const providerInterstitialOpen = accessNested(store, 'showtimes.providerInterstitialOpen', false) || accessNested(store, 'previewPanel.providerInterstitialOpen', false);

		if (!isDesktopSize || (!showtimesVisible && !providerInterstitialOpen && !store.widgetFullyExpanded && !pressKitVisible) && !homeEntVisible || !lastAction && onlyShowtimesAndVideo) {
			return;
		}

		let actionBtnColor = actionData.color || '';

		const iconComponent = {
			close: <Close />,
			back: <ArrowDown />
		};

		const btnAriaLabel = actionData.icon === 'close' ? accessibilityCopy.$CLOSE_SHOWTIMES_ARIA : actionData.icon === 'back' ? accessibilityCopy.$BACK_BTN_SHOWTIMES_ARIA : '';
		const hideActionBtn = this.shouldHideActionBtn(actionData, showtimesVisible, homeEntVisible, pressKitVisible);

		const isShowtimesOnlyIframed = getIsShowtimesOnlyIframed();
		const sonyHeaderEnabled = !isShowtimesOnlyIframed && accessNested(this.props, 'appData.options.sonyFeatures.sonyHeader', false);
		const evidonMode = accessNested(store, 'evidon.bannerOpen');
		const cookieMode = evidonMode || (accessNested(store, 'cookies.pushDown', false) && accessNested(store, 'cookies.cookieBannerOpen', false));
		let buttonStyle = {};
		if (isDesktopSize && (cookieMode || evidonMode)) {
			let cookieHeight = evidonMode ? accessNested(store, 'evidon.height') : accessNested(store, 'cookies.height');
			buttonStyle.left = (((window.innerHeight - cookieHeight - (sonyHeaderEnabled ? 35 : 0)) / 900 * 609) - 20) + 'px';
		}

		return (
			<div class={s.outerActionButton} key="action-button" style={buttonStyle}>
				<Button
					type="primary"
					class={joinClasses(
						s.actionButton,
						s[actionData.icon],
						hideActionBtn && s.hidden,
						homeEntMerchantOpen && s.homeEntMerchantOpen,
						homeEntVisible && s.homeEntBtn,
						pressKitVisible && s.pressKitBtn,
						providerInterstitialOpen && s.providerInterstitial
					)}
					trigger={() => this.triggerActionButton(homeEntVisible)}
					style={{ backgroundColor: actionBtnColor }}
					ariaLabel={btnAriaLabel}
				>
					{iconComponent[actionData.icon]}
				</Button>
			</div>
		);
	}

	renderRegionSelector() {
		let regions = this.state.regions;
		if (!regions) {
			return;
		}
		const appData = this.props?.appData || {};
		const currentRegion = appData?.meta?.region?.slug;
		const useLanguage = appData?.header?.powsterHeader?.regionSelector?.useLanguage;
		let regionSelectorCta = appData?.copy?.baseCopy?.$REGION_SELECTOR_COPY || 'Choose an available region';
		if (useLanguage) {
			regionSelectorCta = appData?.copy?.baseCopy?.$REGION_SELECTOR_LANG_COPY || 'Choose your language';
		}
		const fontStyles = appData?.styling?.fontVariables || {};
		const accessibilityCopy = appData?.copy?.accessibilityCopy || {};

		let allRegions = Object.keys(regions).filter(region => regions[region] && regions[region].url).sort();
		if (!allRegions.length || (allRegions.length === 1 && allRegions[0] === currentRegion)) {
			return;
		}

		let regionSelectorStyle = {};
		const footerHeight = this.props?.footerData?.coveredScreenSize;
		if (footerHeight) {
			regionSelectorStyle = {
				bottom: `${footerHeight}px`
			};
		}

		return (
			<div class={joinClasses(s.regionSelectorOverlay, this.state.regionSelectorShowing && s.showing)} key="regionSelectorOverlay">
				<div class={s.backdrop} onClick={this.toggleRegionSelector} />
				<div class={s.regionSelector} style={regionSelectorStyle}>
					<div class={joinClasses(s.cta, fontStyles.regionSelectTitle)}>{regionSelectorCta}</div>
					<Button
						class={s.closeRegionSelector}
						trigger={this.toggleRegionSelector}
						ariaLabel={accessibilityCopy.$REGION_SELECTOR_CLOSE_BTN_ARIA}
					>
						<Close />
					</Button>
					<div class={s.regions}>
						{allRegions.map(region => {
							let data = regions[region];
							let defaultRegionName = region.replace(/_(.+)$/, ' ($1)').toUpperCase();
							let defaultLangName = data.languageFull || data.language;
							let name = useLanguage ? (data.label || defaultLangName || defaultRegionName) : (data.label || defaultRegionName);
							if (region === currentRegion) {
								return (
									<Button
										class={joinClasses(s.region, s.active)}
										type="tag"
										active
										trigger={this.toggleRegionSelector}
										ariaLabel={accessibilityCopy.$REGION_BTN_CURRENT_REGION_ARIA.replace('<country>', region.toUpperCase())}
									>
										{name}
									</Button>
								);
							}
							return (
								<Link
									class={joinClasses(s.region, fontStyles.regionSelectCountryCodes)}
									href={data.url}
									onClick={() => Store.emit('REGION_SELECTOR_CLICKED', currentRegion, name)}
									ariaLabel={accessibilityCopy.$REGION_BTN_ARIA.replace('<country>', region.toUpperCase())}
								>
									{name}
								</Link>
							);
						})}
					</div>
				</div>
			</div>
		);
	}

	renderPersistentButton() {
		const { isCalendarRemindMode, appData, calRemindOpen, headerData } = this.props;
		const { showtimesOverlay, homeEntOverlay, pressKitOverlay } = headerData;
		const { copy, pages } = appData;
		const { boltShowtimes } = pages;
		const store = Store.get();
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');

		const persistentButtonOverride = accessNested(this.props, 'appData.header.powsterHeader.persistentButton', {});
		const excludeFromSection = this.isPersistentBtnExcluded(persistentButtonOverride);
		const sectionSpecificStyle = this.getSectionSpecificStyle();
		const persistentBtnStyle = {};
		const previewProviderInterstitialOpen = accessNested(store, 'previewPanel.providerInterstitialOpen', false);
		const widgetFullyExpanded = store.widgetFullyExpanded;

		if (sectionSpecificStyle) {
			const { persistentCTAHeaderButtonBg, persistentCTAHeaderButtonBgHover, persistentCTAHeaderButtonTxtColor } = sectionSpecificStyle;
			persistentBtnStyle.background = (this.state.persistentBtnHovered ? persistentCTAHeaderButtonBgHover : persistentCTAHeaderButtonBg) || '';
			persistentBtnStyle.color = persistentCTAHeaderButtonTxtColor || '';
		}

		if (persistentButtonOverride.path || persistentButtonOverride.label) {
			const { path, label, sameWindow } = persistentButtonOverride;
			const copyVariable = accessNested(appData, `copy.baseCopy.${persistentButtonOverride.label}`, persistentButtonOverride.label);

			return (
				<Button
					style={persistentBtnStyle}
					onMouseEnter={() => this.setState({ persistentBtnHovered: true })}
					onMouseLeave={() => this.setState({ persistentBtnHovered: false })}
					class={joinClasses(s.persistentBtn, (showtimesOverlay || homeEntOverlay || pressKitOverlay || widgetFullyExpanded || excludeFromSection || previewProviderInterstitialOpen) && s.hide, fontStyles.headerCta)}
					trigger={e => this.triggerPersistentButton(path, label, sameWindow, e)}
					type="floating"
					// ariaLabel={copyVariable}
					key="persistentButton"
				>
					<HTML
						class={s.persistentBtnLabel}
						content={copyVariable}
						markdown
					/>
				</Button>
			);
		}

		if (isCalendarRemindMode) {
			// Reminder mode
			const copyVariable = accessNested(appData, 'header.powsterHeader.calendars.btnCopy');
			let display = accessNested(copy, ['baseCopy', copyVariable]) || copyVariable;
			if (!display || display.startsWith('$')) {
				display = 'Save to Calendar';
			}
			if (accessNested(appData, 'header.powsterHeader.calendars', null)) {
				return (
					<Button
						style={persistentBtnStyle}
						onMouseEnter={() => this.setState({ persistentBtnHovered: true })}
						onMouseLeave={() => this.setState({ persistentBtnHovered: false })}
						class={joinClasses(s.persistentBtn, (showtimesOverlay || homeEntOverlay || calRemindOpen || excludeFromSection || previewProviderInterstitialOpen || widgetFullyExpanded) && s.hide, fontStyles.headerCta)}
						trigger={e => this.triggerPersistentButton('@calendarreminder', display, true, e)}
						type="floating"
						ariaLabel={copy.accessibilityCopy.$CALENDAR_REMINDER_BTN_ARIA}
						key="remind"
					>
						<HTML
							class={s.persistentBtnLabel}
							content={display}
							markdown
						/>
					</Button>
				);
			}
		} else if (boltShowtimes) {
			if (boltShowtimes.ageRestricted && this.props.client?.ofAge === false) {
				return;
			}

			const display = accessNested(copy, 'baseCopy.$FIND_CINEMAS') || 'Find Cinemas';
			return (
				<Button
					style={persistentBtnStyle}
					onMouseEnter={() => this.setState({ persistentBtnHovered: true })}
					onMouseLeave={() => this.setState({ persistentBtnHovered: false })}
					class={joinClasses(s.persistentBtn, (showtimesOverlay || homeEntOverlay || excludeFromSection || previewProviderInterstitialOpen || widgetFullyExpanded) && s.hide, fontStyles.headerCta)}
					trigger={e => this.triggerPersistentButton('boltShowtimes', display, true, e)}
					type="floating"
					ariaLabel={copy.accessibilityCopy.$FIND_CINEMAS_BTN_ARIA}
					key="showtimes"
				>
					<HTML
						class={s.persistentBtnLabel}
						content={display}
						markdown
					/>
				</Button>
			);
		}
	}

	renderShare() {
		if (!accessNested(this.props, 'appData.header.powsterHeader.share')) {
			return;
		}
		const copy = this.props.appData.copy;
		const isDesktopSize = accessNested(this.props, 'client.isDesktopSize');
		const headerData = this.props.headerData || {};
		const store = Store.get();
		const appData = store.appData || {};
		const fontStyles = appData.styling?.fontVariables;
		const providerInterstitialOpen = accessNested(store, 'showtimes.providerInterstitialOpen');
		const isMobile = accessNested(this.props, 'client.device', '') !== 'desktop';
		const homeActive = isMobile ? store.activePage === 'boltHome' : (store.homeActivePercentage || 0) > getPercentageThreshold(this.props.offsetTop);
		const dualViewEnabled = isDualViewEnabled();
		const dualView = dualViewEnabled && (store.showtimes?.show || store.homeEnt?.show);
		const sectionSpecificStyle = this.getSectionSpecificStyle();
		const shareStyle = {};
		if (sectionSpecificStyle && !headerData.open) {
			const { headerShareMovie, headerShareMovieMob } = sectionSpecificStyle;
			shareStyle.color = (headerShareMovieMob && !isDesktopSize) ? headerShareMovieMob : headerShareMovie;
		}

		const hideShare = getIsSinglePageIframed() || headerData.showtimesSearchOverlay || (headerData.homeEntMerchantOpen && isDesktopSize) || headerData.interstitialOpen;

		return [
			<Button
				style={shareStyle}
				class={joinClasses({
					s,
					shareBtn: true,
					showtimesShare: headerData.showtimesOverlay && !headerData.open,
					navOpen: headerData.open,
					homeScrolled: !homeActive,
					providerInterstitialOpen: providerInterstitialOpen,
					homeEntShare: headerData.homeEntOverlay,
					pressKitShare: headerData.pressKitOverlay,
					hide: hideShare,
					inactive: this.props.calRemindOpen,
					video: headerData.videoOverlayOpen,
					dualView
				})}
				trigger={this.toggleShare}
				ariaLabel={copy.accessibilityCopy.$OPEN_SHARE_ARIA}
				key="toggle-share"
			>
				<span class={joinClasses(s.cta, fontStyles.headerShare)}>{copy.baseCopy.$SHARE}</span>
				<Share class={s.share} aria-hidden />
			</Button>,
			<SharePopout
				appData={this.props.appData}
				shareOpen={headerData.shareOpen}
				toggleSharePop={this.toggleShare}
				shareDetails={headerData.shareDetails}
				closeBtnstyle={this.getCurrentHeaderAction().shareBtn}
				key="share"
			/>
		];
	}

	renderHeaderLogo() {
		const headerLogo = this.props?.appData?.header?.powsterHeader?.logo;
		const headerLogoExclusions = (headerLogo?.pageExclusions || '').split(',')?.map(e => e.trim())?.filter(e => e);
		const headerLogoInclusions = (headerLogo?.pageInclusions || '').split(',')?.map(e => e.trim())?.filter(e => e);
		const isDesktopSize = this.props?.client?.isDesktopSize;
		const activePage = Store.get().activePage;

		let hideHeaderLogo;
		if (headerLogoInclusions.length > 0) {
			hideHeaderLogo = !headerLogoInclusions.includes(activePage);
		} else {
			hideHeaderLogo = headerLogoExclusions.includes(activePage);
		}

		if (headerLogo && isDesktopSize) {
			return (
				<Button
					class={joinClasses(s.headerLogo, hideHeaderLogo && s.hide, !headerLogo.path && s.unclickable)}
					trigger={e => headerLogo.path && this.triggerPersistentButton(headerLogo.path, 'Logo', headerLogo.sameWindow, e)}
					key="headerLogo"
				>
					<img src={headerLogo.src} alt={headerLogo.alt} />
				</Button>
			);
		}
	}

	renderSonyHeader() {
		const props = this.props;
		const appData = props.appData;
		if (!appData?.options?.sonyFeatures?.sonyHeader || getIsSinglePageIframed()) {
			return;
		}
		const sharedMedia = appData?.urls?.roots?.sharedMedia || '';
		let sonyPicturesLink = appData?.copy.baseCopy.$SONY_PICTURES_LINK || 'https://www.sonypictures.com/';
		let sonyLink = appData?.copy.baseCopy.$SONY_LINK || 'https://www.sony.com/en/';

		const a11y = appData?.copy.accessibilityCopy;

		let languageSelector = null;
		const region = appData?.meta?.region?.slug;
		if (['ca', 'ca_fr'].includes(region)) {
			let isFrench = region === 'ca_fr';
			let currentLanguage = isFrench ? 'french' : 'english';
			let otherUrl = this.state.regions?.[isFrench ? 'english' : 'french']?.url;
			if (otherUrl) {
				const one = (lang, nameFull) => {
					if (lang === currentLanguage) {
						return <span class={joinClasses(s.language, s.active)}>{nameFull}</span>;
					}
					return <a class={s.language} href={this.state.regions?.[lang]?.url}>{nameFull}</a>;
				};
				languageSelector = (
					<div class={s.sonyCALanguageSelector}>
						{one('english', 'English')}
						<span class={s.separator}>|</span>
						{one('french', 'Français')}
					</div>
				);
			}

		}

		return (
			<div
				class={s.sony}
				style={{
					position: 'absolute',
					top: props.offsetTop ? `${props.offsetTop - 34}px` : '',
					zIndex: 9
				}}
			>
				<div class={s.left}>
					<Link class={!sonyPicturesLink && s.disabled} target="_blank" href={sonyPicturesLink} ariaLabel={a11y?.$SONY_PICTURES_LINK_ARIA_LABEL}>
						{/* Fallback for Safari */}
						<img class={s.sonyPicturesLogo} src={`${sharedMedia}/sonypictures/images/sony-pictures-logo.svg`} alt={a11y?.$SONY_PICTURES_LOGO_ALT} />
					</Link>
				</div>
				<div class={s.right}>
					{languageSelector}
					<Link class={!sonyLink && s.disabled} target="_blank" href={sonyLink} ariaLabel={a11y?.$SONY_LINK_ARIA_LABEL}>
						<img class={s.sonyLogo} src={`${sharedMedia}/sonypictures/images/sony-logo.svg`} alt={a11y?.$SONY_LOGO_ALT} />
					</Link>
				</div>
			</div>
		);
	}

	render(props) {
		const {
			appData,
			headerData,
			isCalendarRemindMode,
			client,
			solidBackground,
			desktopSolidHeader,
			style,
			offsetTop,
			calRemindOpen
		} = props;
		const { copy } = appData;
		const {
			showtimesOverlay,
			homeEntOverlay,
			pressKitOverlay,
			homeEntMerchantOpen,
			activeSection,
			videoOverlayOpen,
			showtimesSearchOverlay,
			open: navOpen,
			shareOpen,
			interstitialOpen
		} = headerData || {};

		const store = Store.get();
		const isMobile = accessNested(this.props, 'client.device', '') !== 'desktop';
		const homeActive = isMobile ? Store.get().activePage === 'boltHome' : accessNested(store, 'homeActivePercentage', 1) > getPercentageThreshold(offsetTop);

		const locationClassName = videoOverlayOpen ? 'boltVideos' : (activeSection || 'bolt');
		const actionData = this.getCurrentHeaderAction();

		const regionSelector = this.renderRegionSelector();
		const regionSelectorEnabled = typeof accessNested(appData, 'header.powsterHeader.regionSelector') !== 'undefined';
		// Should be set at studio level
		const { regionSelectorShowing } = this.state;

		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');

		const oneSheetOverlay = accessNested(store, 'synopsis.onesheetExpanded');
		const trailerOverlayOpen = accessNested(store, 'trailerOverlay.overlayOpen');

		// const headerLogo = accessNested(appData, 'header.powsterHeader.logo', false);
		// const headerLogoExclusions = (headerLogo?.pageExclusions || '').split(',')?.map(e => e.trim())?.filter(e => e);
		// const hideHeaderLogo = headerLogoExclusions.includes(store.activePage);
		const hideBurgerMenu = accessNested(Store.get(), 'appData.options.disableBurgerMenu', false);
		const isDesktopSize = accessNested(this.props, 'client.isDesktopSize');


		let containerStyle = {};
		if (offsetTop) {
			containerStyle.position = 'fixed';
			containerStyle.top = offsetTop + 'px';
			containerStyle.zIndex = 9;
		}

		let sonyHeader = this.renderSonyHeader();

		if (!accessNested(this.props, 'appData.header.powsterHeader')) {
			return sonyHeader;
		}

		let burgerMenuAriaLabel;

		// Should probably set the Aria label when setting the action (when calling SET_HEADER_ACTION)
		if (actionData.icon === 'burger') {
			burgerMenuAriaLabel = copy.accessibilityCopy.$OPEN_NAV_ARIA;
		} else if (actionData.tag === 'closeNavMenu') {
			burgerMenuAriaLabel = copy.accessibilityCopy.$CLOSE_NAV_ARIA;
		} else if (actionData.tag === 'closeOnesheetOverlay') {
			burgerMenuAriaLabel = copy.accessibilityCopy.$CLOSE_ONESHEET_ARIA;
		} else if (actionData.tag === 'closeCalRemind') {
			burgerMenuAriaLabel = copy.accessibilityCopy.$CALENDAR_REMINDER_CLOSE_ARIA;
		} else if (actionData.tag === 'closeVideoOverlay') {
			burgerMenuAriaLabel = copy.accessibilityCopy.$CLOSE_VIDEOS_ARIA;
		} else if (actionData.icon === 'back') {
			burgerMenuAriaLabel = copy.accessibilityCopy.$BACK_BTN_SHOWTIMES_ARIA;
		}

		const navDisabled = !this.props?.appData?.header?.powsterHeader?.nav;
		const isSinglePageIframed = getIsSinglePageIframed();
		const menuText = copy.baseCopy.$NAVIGATION_LABEL;
		const showMenuText = menuText && !showtimesOverlay && !homeEntOverlay && !videoOverlayOpen && !navDisabled;

		const sectionSpecificStyle = this.getSectionSpecificStyle();
		const navLabelStyle = {};
		if (sectionSpecificStyle) {
			const { headerNavLabel } = sectionSpecificStyle;
			navLabelStyle.color = headerNavLabel;
		}

		const burgerStyle = {};
		if (sectionSpecificStyle && !headerData.open) {
			const { headerNavIconOpen, headerNavIconOpenMob } = sectionSpecificStyle;
			burgerStyle.background = (headerNavIconOpenMob && !isDesktopSize) ? headerNavIconOpenMob : headerNavIconOpen;
		}

		const isBurgerIcon = !actionData.icon || actionData.icon === 'burger';

		return (
			<div
				class={joinClasses(`header-${store.activePage}`, navOpen && 'navOpen', {
					s,
					headerContainer: true,
					homeEntMerchantOpen
				})}
			>
				{sonyHeader}
				<div
					class={
						joinClasses({
							s,
							container: true,
							showtimesSearchOverlay,
							solidBackground,
							desktopSolidHeader
						}, s[`activeSection--${locationClassName}`])
					}
					style={containerStyle}
					key="container"
					id="header"
				>
					<Button
						class={joinClasses(
							s.burgerMenuBtn,
							(shareOpen || regionSelectorShowing || navDisabled || hideBurgerMenu && isBurgerIcon) && s.hide,
							isSinglePageIframed && s.disabled
						)}
						trigger={this.triggerBurgerBtn}
						ariaLabel={burgerMenuAriaLabel}
						key="burgerButton"
					>
						<BurgerMenu
							type={actionData.icon || 'burger'}
							dark={actionData.dark}
							style={actionData.style}
							hidden={interstitialOpen}
							lineStyle={burgerStyle}
							showtimesOverlayOpen={showtimesOverlay}
							homeEntOverlayOpen={homeEntOverlay}
							pressKitOverlayOpen={pressKitOverlay}
							isSinglePageIframed={isSinglePageIframed}
							homeScrolled={!homeActive}
							navOpen={navOpen}
							showtimesSearchOverlay={showtimesSearchOverlay}
							videoOverlayOpen={videoOverlayOpen}
							oneSheetOverlayOpen={oneSheetOverlay}
							trailerOverlayOpen={trailerOverlayOpen}
							calendarRemindOpen={calRemindOpen}
							widgetFullyExpanded={store.widgetFullyExpanded}
						/>
					</Button>
					{this.renderHeaderLogo()}
					{!!showMenuText && (
						<span class={joinClasses(s.menuText, fontStyles.headerShare, { s, homeScrolled: !homeActive })} style={navLabelStyle} onClick={this.triggerBurgerBtn}>
							{menuText}
						</span>
					)}
					{this.renderExtraActions()}
					{this.renderPersistentButton()}
					{this.renderShare()}
					<HeaderExpanded
						appData={appData}
						activePage={props.activePage}
						headerData={headerData}
						navOpen={navOpen}
						client={client}
						isCalendarRemindMode={isCalendarRemindMode}
						showRegionSelector={regionSelector && regionSelectorEnabled}
						regionSelectorClick={this.toggleRegionSelector}
						style={style}
						key="expand"
					/>
					{regionSelector}
				</div>
			</div>
		);
	}
}
